import React, { useState, useEffect } from 'react';
import { FaHospital, FaIdCard, FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ProcedurePatientEntry = ({ setStep, selectedProcedureType, procedureData, setProcedureData }) => {
  const [patientId, setPatientId] = useState('');
  const [surgeryId, setSurgeryId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate surgery ID when component mounts
  useEffect(() => {
    const generateSurgeryId = () => {
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 1000);
      return `SRG-${timestamp}-${random}`;
    };
    
    const newSurgeryId = generateSurgeryId();
    setSurgeryId(newSurgeryId);
    
    // Initialize procedure data
    if (setProcedureData) {
      setProcedureData({
        procedureId: newSurgeryId,
        procedureType: selectedProcedureType || '',
        patientID: '',
        startTime: null,
        endTime: null,
        duration: null,
        status: 'in_progress',
        completed: false
      });
    }
  }, [selectedProcedureType, setProcedureData]);

  const handleBeginProcedure = () => {
    if (!patientId.trim()) {
      toast.error('Please enter a Patient ID');
      return;
    }

    setIsSubmitting(true);
    
    // Update procedure data with patient ID and start time
    const startTime = new Date();
    if (setProcedureData) {
      setProcedureData(prev => ({
        ...prev,
        patientID: patientId.trim(),
        startTime: startTime.toISOString(),
        status: 'in_progress'
      }));
    }
    
    // Small delay to show loading state
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('procedureTimer');
    }, 300);
  };

  const handleBack = () => {
    setStep('procedureSelection');
  };

  // Get procedure type display name
  const getProcedureTypeName = () => {
    const procedureNames = {
      'extraction': 'Tooth Extraction',
      'implant': 'Dental Implant',
      'biopsy': 'Oral Biopsy',
      'cyst': 'Cyst Removal',
      'fracture': 'Fracture Repair',
      'orthognathic': 'Orthognathic Surgery',
      'tmj': 'TMJ Surgery',
      'reconstruction': 'Facial Reconstruction',
      'other': 'Other Procedure'
    };
    return procedureNames[selectedProcedureType] || 'Procedure';
  };

  return (
    <div className="flex min-h-screen flex-col justify-center bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Header with Icon */}
        <div className="flex justify-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-teal-100">
            <FaHospital className="size-8 text-teal-600" aria-hidden="true" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
          Begin Procedure
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {selectedProcedureType ? getProcedureTypeName() : 'Enter patient information to start'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-10 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={(e) => { e.preventDefault(); handleBeginProcedure(); }} className="space-y-6">
            {/* Surgery ID Display */}
            <div>
              <label htmlFor="surgeryId" className="block text-sm font-medium text-gray-900">
                Procedure ID (Auto-Generated)
              </label>
              <div className="mt-2">
                <div className="flex items-center gap-3 rounded-md bg-gray-50 px-3 py-2.5 border border-gray-200">
                  <FaIdCard className="size-5 text-gray-400" />
                  <span className="text-sm font-mono text-gray-900">{surgeryId}</span>
                </div>
              </div>
            </div>

            {/* Patient ID Input */}
            <div>
              <label htmlFor="patientId" className="block text-sm font-medium text-gray-900">
                Patient ID <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="patientId"
                  name="patientId"
                  type="text"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  placeholder="Enter Patient ID"
                  required
                  autoFocus
                  className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-4 pt-4">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
              >
                <FaArrowLeft className="size-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !patientId.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Starting...
                  </>
                ) : (
                  <>
                    <FaHospital className="size-4" />
                    Begin Procedure
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProcedurePatientEntry;
